import ArrowOutwardRoundedIcon from "@mui/icons-material/ArrowOutwardRounded";
import { Box, Button, Typography } from "@mui/material";
import { motion, useReducedMotion } from "framer-motion";
import { useLocation, useNavigate } from "react-router-dom";
import aboutUs1 from "../Images/aboutUs1.png";
import aboutUs2 from "../Images/aboutUs2.png";
import { ICommonState } from "../constant";
import SectionHeading from "../Shared/SectionHeading";

const fadeInVariantsLeft = {
  hidden: { opacity: 0, x: -32 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.7 } },
};

const fadeInVariantsRight = {
  hidden: { opacity: 0, x: 32 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.7 } },
};

const featureBlocks = [
  {
    title: "We Design Spaces for Elegant, Stylish Living",
    points: [
      "Thoughtful layouts that balance comfort, storage, and daily usability.",
      "Bespoke palettes and material combinations curated for your lifestyle.",
      "Sustainable selections for healthier, long-lasting interiors.",
      "A detail-focused process from concept development to final handover.",
    ],
    image: aboutUs2,
    reverse: true,
    cta: "Discuss Your Vision",
  },
  {
    title: "Discover Our Expertise",
    points: [
      "Experienced design and project teams aligned on quality and timelines.",
      "Clear planning and coordination across carpentry, lighting, and finishes.",
      "Custom solutions for residential, commercial, and hospitality projects.",
      "Execution-first approach that keeps intent and practicality in sync.",
    ],
    image: aboutUs1,
    reverse: false,
    cta: "Start Consultation",
  },
];

export const AboutUs: React.FC<ICommonState> = ({ setIsModalOpen }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const isAboutPage = location.pathname === "/about-us";
  const prefersReducedMotion = useReducedMotion();

  return (
    <Box
      id="about-us-section"
      sx={{
        px: { xs: 1.2, sm: 1.8, md: 2.6 },
        py: { xs: 4.6, md: 5.8 },
        background: isAboutPage
          ? "linear-gradient(180deg, #f8f3ee 0%, #fcfaf7 100%)"
          : "transparent",
      }}
    >
      <SectionHeading
        eyebrow="Who We Are"
        title="Designing Meaningful Interiors with Character"
        subtitle="Every project is shaped around your routines, aspirations, and spatial needs so the result feels personal, timeless, and practical."
        titleAs={isAboutPage ? "h1" : "h2"}
      />

      <Box
        sx={{
          width: "100%",
          maxWidth: "none",
          mx: "auto",
          display: "flex",
          flexDirection: "column",
          gap: { xs: 2.2, md: 2.8 },
        }}
      >
        {featureBlocks.map((block, index) => (
          <motion.div
            key={block.title}
            initial={prefersReducedMotion ? false : "hidden"}
            whileInView={prefersReducedMotion ? undefined : "visible"}
            viewport={{ once: true, amount: 0.2 }}
            variants={block.reverse ? fadeInVariantsRight : fadeInVariantsLeft}
            whileHover={prefersReducedMotion ? undefined : { y: -4 }}
          >
            <Box
              sx={{
                borderRadius: { xs: "22px", md: "28px" },
                p: { xs: 1.4, sm: 1.8, md: 2.2 },
                background:
                  "linear-gradient(152deg, rgba(255,255,255,0.86), rgba(255,247,243,0.72))",
                border: "1px solid rgba(128,51,65,0.13)",
                boxShadow: "0 16px 38px rgba(20,8,10,0.1)",
              }}
            >
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", md: "0.95fr 1.05fr" },
                  gap: { xs: 2.1, md: 3.1 },
                  alignItems: "stretch",
                }}
              >
                <Box
                  sx={{
                    borderRadius: "18px",
                    overflow: "hidden",
                    minHeight: { xs: 240, sm: 300, md: "100%" },
                    order: block.reverse ? { xs: 1, md: 2 } : { xs: 1, md: 1 },
                  }}
                >
                  <Box
                    component="img"
                    src={block.image}
                    alt={`Vista Fusion Studio ${index + 1}`}
                    loading="lazy"
                    decoding="async"
                    sx={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                      transition: "transform 320ms ease",
                      "&:hover": {
                        transform: "scale(1.04)",
                      },
                    }}
                    onContextMenu={(event) => event.preventDefault()}
                  />
                </Box>

                <Box
                  sx={{
                    px: { xs: 0.4, sm: 0.8, md: 1.3 },
                    py: { xs: 1.1, md: 1.2 },
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    order: block.reverse ? { xs: 2, md: 1 } : { xs: 2, md: 2 },
                  }}
                >
                  <Typography
                    component="h3"
                    sx={{
                      fontFamily: '"Cormorant Garamond", serif',
                      color: "#32171d",
                      fontWeight: 700,
                      lineHeight: 1.06,
                      letterSpacing: "0.01em",
                      fontSize: { xs: "1.72rem", sm: "2rem", md: "2.45rem" },
                    }}
                  >
                    {block.title}
                  </Typography>

                  <Box component="ul" sx={{ mt: 1.6, pl: 2.2, mb: 0 }}>
                    {block.points.map((text) => (
                      <Typography
                        key={text}
                        component="li"
                        sx={{
                          mb: 1.05,
                          color: "rgba(53,32,35,0.82)",
                          fontFamily: '"Manrope", sans-serif',
                          fontSize: { xs: "0.9rem", md: "0.96rem" },
                          lineHeight: 1.7,
                        }}
                      >
                        {text}
                      </Typography>
                    ))}
                  </Box>

                  <Box sx={{ mt: 1.6 }}>
                    <Button
                      onClick={() => setIsModalOpen(true)}
                      endIcon={<ArrowOutwardRoundedIcon />}
                      sx={{
                        borderRadius: "999px",
                        px: 2.4,
                        py: 1.02,
                        textTransform: "none",
                        fontFamily: '"Manrope", sans-serif',
                        fontWeight: 700,
                        color: "#fff",
                        background:
                          "linear-gradient(137deg, #803341 0%, #a85156 100%)",
                        boxShadow: "0 10px 18px rgba(128,51,65,0.28)",
                        "&:hover": {
                          background:
                            "linear-gradient(137deg, #6f2c39 0%, #954349 100%)",
                        },
                      }}
                    >
                      {block.cta}
                    </Button>
                  </Box>
                </Box>
              </Box>
            </Box>
          </motion.div>
        ))}
      </Box>

      {isAboutPage ? (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 4.6 }}>
          <Button
            variant="outlined"
            onClick={() => navigate("/")}
            sx={{
              px: 2.6,
              py: 1.02,
              borderRadius: "999px",
              textTransform: "none",
              borderColor: "rgba(128,51,65,0.45)",
              color: "#672936",
              fontFamily: '"Manrope", sans-serif',
              fontWeight: 700,
              "&:hover": {
                borderColor: "#803341",
                backgroundColor: "rgba(128,51,65,0.07)",
              },
            }}
          >
            Back to Home
          </Button>
        </Box>
      ) : null}
    </Box>
  );
};
